/**
 * v4.37.0 — Mission Control Broadcast Center admin page (Phase 2).
 *
 * Super-admin authoring console. Two-panel layout:
 *   • Left  — list of all broadcasts (filterable by status)
 *   • Right — editor with trilingual tabs, priority/surface/audience/
 *             frequency selectors, optional CTA, optional active window,
 *             and a live PREVIEW that renders the broadcast through the
 *             ACTUAL production surface component (inline / sticky / modal).
 *
 * Design philosophy: lightweight, low-friction, fast feedback loop.
 * Preview-before-publish is the contract — what you see in the preview
 * is byte-identical to what the ecosystem will see in production.
 *
 * data-testid coverage on every interactive element so the testing
 * agent can drive the form end-to-end.
 */
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Megaphone, Plus, Pencil, Trash2, Send, Eye, Loader2,
    Globe, Calendar, AlertTriangle, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { broadcastsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BroadcastPreview } from '../components/broadcasts/BroadcastPreview';

const LANGS = ['en', 'fr', 'es'];

const EMPTY_FORM = {
    title: { en: '', fr: '', es: '' },
    body: { en: '', fr: '', es: '' },
    cta_enabled: false,
    cta: { label: { en: '', fr: '', es: '' }, url: '' },
    priority: 'normal',
    surface: 'inline',
    audience: 'everyone',
    frequency: 'until_dismissed',
    active_from: '',
    active_until: '',
};

const AdminBroadcasts = () => {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);  // broadcast being edited, or null for new
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchList = async () => {
        try {
            setLoading(true);
            const params = filter === 'all' ? undefined : filter;
            const res = await broadcastsAPI.listAdmin(params);
            setList(res.data);
        } catch (e) {
            toast.error('Failed to load broadcasts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [filter]);

    // v4.40.1 — Deep-link: when navigated from Timeline (or any caller)
    // with ?selected=<broadcast_id>, auto-open the editor on that row
    // and then strip the query param so a refresh doesn't keep
    // re-opening the same record.
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkSelected = searchParams.get('selected');
    useEffect(() => {
        if (!deepLinkSelected || loading || !list.length) return;
        const target = list.find((b) => b.id === deepLinkSelected);
        if (target) {
            startEdit(target);
            const next = new URLSearchParams(searchParams);
            next.delete('selected');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkSelected, loading, list]);

    const startNew = () => {
        setSelected(null);
        setForm(EMPTY_FORM);
        setShowPreview(false);
    };

    const startEdit = (b) => {
        setSelected(b);
        setForm({
            title: b.title || EMPTY_FORM.title,
            body: b.body || EMPTY_FORM.body,
            cta_enabled: !!b.cta,
            cta: b.cta || EMPTY_FORM.cta,
            priority: b.priority,
            surface: b.surface,
            audience: b.audience,
            frequency: b.frequency,
            active_from: b.active_from || '',
            active_until: b.active_until || '',
        });
        setShowPreview(false);
    };

    // -----------------------------------------------------------------
    // Build payload — strips empties and shapes for the backend schema.
    // Localized fields are only validated for non-empty when we attempt
    // to save (drafts allow partial work, but the schema needs all 3
    // languages — so we validate here before hitting the API).
    // -----------------------------------------------------------------
    const buildPayload = () => {
        const payload = {
            title: form.title,
            body: form.body,
            priority: form.priority,
            surface: form.surface,
            audience: form.audience,
            frequency: form.frequency,
            active_from: form.active_from || null,
            active_until: form.active_until || null,
        };
        if (form.cta_enabled) {
            payload.cta = { label: form.cta.label, url: form.cta.url.trim() };
        } else {
            payload.cta = null;
        }
        return payload;
    };

    const validate = (payload) => {
        const missing = [];
        for (const lang of LANGS) {
            if (!payload.title[lang]?.trim()) missing.push(`title.${lang}`);
            if (!payload.body[lang]?.trim())  missing.push(`body.${lang}`);
        }
        if (payload.cta) {
            if (!payload.cta.url?.trim()) missing.push('cta.url');
            for (const lang of LANGS) {
                if (!payload.cta.label[lang]?.trim()) missing.push(`cta.label.${lang}`);
            }
            const u = payload.cta.url.trim();
            if (u && !u.startsWith('/') && !u.startsWith('https://')) {
                return { ok: false, missing, urlError: true };
            }
        }
        return { ok: missing.length === 0, missing, urlError: false };
    };

    const save = async (alsoPublish = false) => {
        const payload = buildPayload();
        if (payload.cta) {
            // Remove cta entirely if all fields blank (allow easy "no CTA")
            const anyContent = payload.cta.url?.trim() || LANGS.some((l) => payload.cta.label[l]?.trim());
            if (!anyContent) payload.cta = null;
        }
        const v = validate(payload);
        if (!v.ok) {
            toast.error(t('broadcasts.toasts.save_failed'));
            return;
        }
        try {
            setSaving(true);
            let saved;
            if (selected) {
                const updatePayload = { ...payload, clear_cta: payload.cta == null };
                saved = (await broadcastsAPI.update(selected.id, updatePayload)).data;
                toast.success(t('broadcasts.toasts.updated'));
            } else {
                saved = (await broadcastsAPI.create(payload)).data;
                toast.success(t('broadcasts.toasts.created'));
            }
            if (alsoPublish) {
                await broadcastsAPI.publish(saved.id);
                toast.success(t('broadcasts.toasts.published'));
            }
            await fetchList();
            setSelected(null);
            setForm(EMPTY_FORM);
            setShowPreview(false);
        } catch (e) {
            const detail = e?.response?.data?.detail || t('broadcasts.toasts.save_failed');
            toast.error(typeof detail === 'string' ? detail : t('broadcasts.toasts.save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const togglePublish = async (b) => {
        try {
            if (b.status === 'published') {
                await broadcastsAPI.unpublish(b.id);
                toast.success(t('broadcasts.toasts.unpublished'));
            } else {
                await broadcastsAPI.publish(b.id);
                toast.success(t('broadcasts.toasts.published'));
            }
            await fetchList();
            if (selected?.id === b.id) {
                const refreshed = (await broadcastsAPI.getAdmin(b.id)).data;
                setSelected(refreshed);
            }
        } catch (e) {
            toast.error('Action failed');
        }
    };

    const confirmDelete = async () => {
        try {
            await broadcastsAPI.delete(deleteTarget.id);
            toast.success(t('broadcasts.toasts.deleted'));
            if (selected?.id === deleteTarget.id) {
                setSelected(null);
                setForm(EMPTY_FORM);
            }
            setDeleteTarget(null);
            await fetchList();
        } catch (e) {
            toast.error('Delete failed');
        }
    };

    // Preview broadcast object — built from current form state so it
    // reflects unsaved edits the moment the user toggles preview on.
    const previewBroadcast = useMemo(() => {
        const p = buildPayload();
        return {
            id: selected?.id || 'preview',
            ...p,
            cta: p.cta && (p.cta.url?.trim() || LANGS.some((l) => p.cta.label[l]?.trim())) ? p.cta : null,
            status: 'published',
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, selected]);

    if (!isSuperAdmin()) {
        return (
            <div className="p-8 text-center" data-testid="broadcasts-access-denied">
                <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
                <h2 className="text-xl font-semibold mb-1">Super-admin only</h2>
                <p className="text-sm text-muted-foreground">The Broadcast Center is reserved for platform administrators.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto" data-testid="broadcasts-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                         style={{ background: 'linear-gradient(135deg, #4AA3FF22, #B06CFF22)', border: '1px solid #4AA3FF55' }}>
                        <Megaphone className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold" data-testid="broadcasts-title">{t('broadcasts.title')}</h1>
                        <p className="text-sm text-muted-foreground">{t('broadcasts.subtitle')}</p>
                    </div>
                </div>
                <Button onClick={startNew} data-testid="broadcasts-new-btn">
                    <Plus className="w-4 h-4 mr-1" />{t('broadcasts.new')}
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
                {/* Left: list */}
                <div className="space-y-3" data-testid="broadcasts-list">
                    <div className="flex gap-1 flex-wrap">
                        {['all', 'draft', 'published', 'unpublished'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filter === f ? 'bg-sky-500/15 border-sky-500/50 text-sky-600' : 'border-border text-muted-foreground hover:bg-muted'}`}
                                data-testid={`broadcasts-filter-${f}`}
                            >
                                {t(`broadcasts.filters.${f}`)}
                            </button>
                        ))}
                    </div>
                    {loading ? (
                        <div className="text-center py-10 text-muted-foreground">
                            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                        </div>
                    ) : list.length === 0 ? (
                        <div className="border border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground" data-testid="broadcasts-empty">
                            {t('broadcasts.empty')}
                        </div>
                    ) : list.map((b) => (
                        <button
                            key={b.id}
                            onClick={() => startEdit(b)}
                            className={`w-full text-left border rounded-lg p-3 transition-colors ${selected?.id === b.id ? 'border-sky-500 bg-sky-500/5' : 'border-border hover:bg-muted'}`}
                            data-testid={`broadcasts-item-${b.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium truncate">{b.title?.en || '—'}</div>
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">{b.body?.en || ''}</div>
                                </div>
                                <Badge priority={b.priority} status={b.status} t={t} />
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2 text-[10px] text-muted-foreground">
                                <Chip>{t(`broadcasts.surface.${b.surface}`)}</Chip>
                                <Chip>{t(`broadcasts.audience.${b.audience}`)}</Chip>
                                <Chip>{t(`broadcasts.frequency.${b.frequency}`)}</Chip>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Right: editor + preview */}
                <div className="border rounded-xl p-6 bg-card" data-testid="broadcasts-editor">
                    {showPreview ? (
                        <div data-testid="broadcasts-preview-pane">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                                        <Sparkles className="w-3 h-3 inline mr-1" />
                                        {t('broadcasts.preview_label')}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{t('broadcasts.preview_explainer')}</p>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => setShowPreview(false)} data-testid="broadcasts-preview-back">
                                    {t('broadcasts.form.cancel')}
                                </Button>
                            </div>
                            <BroadcastPreview broadcast={previewBroadcast} />
                        </div>
                    ) : (
                        <BroadcastForm
                            form={form}
                            setForm={setForm}
                            saving={saving}
                            selected={selected}
                            onCancel={() => { setSelected(null); setForm(EMPTY_FORM); }}
                            onPreview={() => setShowPreview(true)}
                            onSave={save}
                            onTogglePublish={togglePublish}
                            onDelete={() => setDeleteTarget(selected)}
                            t={t}
                        />
                    )}
                </div>
            </div>

            <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('broadcasts.confirm_delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('broadcasts.confirm_delete_body')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="broadcasts-delete-cancel">{t('broadcasts.form.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} data-testid="broadcasts-delete-confirm">
                            <Trash2 className="w-4 h-4 mr-1" />{t('broadcasts.actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// ----- Small visual atoms ------------------------------------------------

const Chip = ({ children }) => (
    <span className="px-1.5 py-0.5 rounded bg-muted">{children}</span>
);

const Badge = ({ priority, status, t }) => {
    const priColor = priority === 'critical' ? 'bg-red-500/15 text-red-600 border-red-500/50'
        : priority === 'important' ? 'bg-amber-500/15 text-amber-600 border-amber-500/50'
        : 'bg-sky-500/15 text-sky-600 border-sky-500/50';
    return (
        <div className="flex flex-col items-end gap-1 shrink-0">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wider ${priColor}`}>
                {t(`broadcasts.priority.${priority}`)}
            </span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {t(`broadcasts.status.${status}`)}
            </span>
        </div>
    );
};

// ----- Form --------------------------------------------------------------

const BroadcastForm = ({ form, setForm, saving, selected, onCancel, onPreview, onSave, onTogglePublish, onDelete, t }) => {
    const setLocalized = (field, lang, value) =>
        setForm((prev) => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
    const setCtaLabel = (lang, value) =>
        setForm((prev) => ({ ...prev, cta: { ...prev.cta, label: { ...prev.cta.label, [lang]: value } } }));

    return (
        <div className="space-y-5">
            {/* Trilingual content */}
            <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                    <Globe className="w-3 h-3 inline mr-1" />{t('broadcasts.form.language')}
                </Label>
                <Tabs defaultValue="en">
                    <TabsList data-testid="broadcasts-lang-tabs">
                        {LANGS.map((l) => (
                            <TabsTrigger key={l} value={l} data-testid={`broadcasts-lang-${l}`}>{l.toUpperCase()}</TabsTrigger>
                        ))}
                    </TabsList>
                    {LANGS.map((l) => (
                        <TabsContent key={l} value={l} className="space-y-3 pt-3">
                            <div>
                                <Label htmlFor={`title-${l}`}>{t('broadcasts.form.title')}</Label>
                                <Input
                                    id={`title-${l}`}
                                    value={form.title[l]}
                                    onChange={(e) => setLocalized('title', l, e.target.value)}
                                    maxLength={120}
                                    data-testid={`broadcasts-title-${l}`}
                                />
                            </div>
                            <div>
                                <Label htmlFor={`body-${l}`}>{t('broadcasts.form.body')}</Label>
                                <Textarea
                                    id={`body-${l}`}
                                    value={form.body[l]}
                                    onChange={(e) => setLocalized('body', l, e.target.value)}
                                    rows={4}
                                    maxLength={1500}
                                    data-testid={`broadcasts-body-${l}`}
                                />
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>

            {/* Targeting block */}
            <div className="grid grid-cols-2 gap-3">
                <SelectField label={t('broadcasts.form.priority')} value={form.priority}
                    onChange={(v) => setForm((p) => ({ ...p, priority: v }))}
                    options={[['normal', t('broadcasts.priority.normal')], ['important', t('broadcasts.priority.important')], ['critical', t('broadcasts.priority.critical')]]}
                    testId="broadcasts-priority"
                />
                <SelectField label={t('broadcasts.form.surface')} value={form.surface}
                    onChange={(v) => setForm((p) => ({ ...p, surface: v }))}
                    options={[['inline', t('broadcasts.surface.inline')], ['sticky', t('broadcasts.surface.sticky')], ['modal', t('broadcasts.surface.modal')]]}
                    testId="broadcasts-surface"
                />
                <SelectField label={t('broadcasts.form.audience')} value={form.audience}
                    onChange={(v) => setForm((p) => ({ ...p, audience: v }))}
                    options={[['everyone', t('broadcasts.audience.everyone')], ['super_admin', t('broadcasts.audience.super_admin')], ['client_admin', t('broadcasts.audience.client_admin')], ['location_manager', t('broadcasts.audience.location_manager')]]}
                    testId="broadcasts-audience"
                />
                <SelectField label={t('broadcasts.form.frequency')} value={form.frequency}
                    onChange={(v) => setForm((p) => ({ ...p, frequency: v }))}
                    options={[['until_dismissed', t('broadcasts.frequency.until_dismissed')], ['every_login', t('broadcasts.frequency.every_login')]]}
                    testId="broadcasts-frequency"
                />
            </div>

            {/* Active window (optional) */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label htmlFor="active_from"><Calendar className="w-3 h-3 inline mr-1" />{t('broadcasts.form.active_from')}</Label>
                    <Input id="active_from" type="datetime-local"
                        value={form.active_from ? form.active_from.slice(0, 16) : ''}
                        onChange={(e) => setForm((p) => ({ ...p, active_from: e.target.value ? `${e.target.value}:00Z` : '' }))}
                        data-testid="broadcasts-active-from"
                    />
                </div>
                <div>
                    <Label htmlFor="active_until">{t('broadcasts.form.active_until')}</Label>
                    <Input id="active_until" type="datetime-local"
                        value={form.active_until ? form.active_until.slice(0, 16) : ''}
                        onChange={(e) => setForm((p) => ({ ...p, active_until: e.target.value ? `${e.target.value}:00Z` : '' }))}
                        data-testid="broadcasts-active-until"
                    />
                </div>
            </div>

            {/* CTA (optional) */}
            <div className="border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">{t('broadcasts.form.cta_section')}</Label>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{t('broadcasts.form.cta_enable')}</span>
                        <Switch
                            checked={form.cta_enabled}
                            onCheckedChange={(v) => setForm((p) => ({ ...p, cta_enabled: v }))}
                            data-testid="broadcasts-cta-toggle"
                        />
                    </div>
                </div>
                {form.cta_enabled && (
                    <div className="space-y-2">
                        <Tabs defaultValue="en">
                            <TabsList data-testid="broadcasts-cta-lang-tabs">
                                {LANGS.map((l) => (
                                    <TabsTrigger key={l} value={l}>{l.toUpperCase()}</TabsTrigger>
                                ))}
                            </TabsList>
                            {LANGS.map((l) => (
                                <TabsContent key={l} value={l} className="pt-2">
                                    <Label htmlFor={`cta-${l}`}>{t('broadcasts.form.cta_label')}</Label>
                                    <Input id={`cta-${l}`} value={form.cta.label[l]}
                                        onChange={(e) => setCtaLabel(l, e.target.value)}
                                        maxLength={60} data-testid={`broadcasts-cta-label-${l}`} />
                                </TabsContent>
                            ))}
                        </Tabs>
                        <div>
                            <Label htmlFor="cta-url">{t('broadcasts.form.cta_url')}</Label>
                            <Input id="cta-url" value={form.cta.url}
                                onChange={(e) => setForm((p) => ({ ...p, cta: { ...p.cta, url: e.target.value } }))}
                                placeholder="/operational-base or https://example.com"
                                data-testid="broadcasts-cta-url"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button variant="outline" onClick={onPreview} data-testid="broadcasts-preview-btn">
                    <Eye className="w-4 h-4 mr-1" />{t('broadcasts.form.preview')}
                </Button>
                <Button variant="outline" onClick={() => onSave(false)} disabled={saving} data-testid="broadcasts-save-draft-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    {selected && selected.status !== 'draft' ? t('broadcasts.form.save') : t('broadcasts.form.save_draft')}
                </Button>
                {(!selected || selected.status !== 'published') && (
                    <Button onClick={() => onSave(true)} disabled={saving} data-testid="broadcasts-publish-btn">
                        <Send className="w-4 h-4 mr-1" />{t('broadcasts.form.publish')}
                    </Button>
                )}
                {selected && selected.status === 'published' && (
                    <Button variant="outline" onClick={() => onTogglePublish(selected)} data-testid="broadcasts-unpublish-btn">
                        {t('broadcasts.actions.unpublish')}
                    </Button>
                )}
                {selected && (
                    <Button variant="ghost" onClick={onDelete} className="ml-auto text-destructive hover:text-destructive" data-testid="broadcasts-delete-btn">
                        <Trash2 className="w-4 h-4 mr-1" />{t('broadcasts.actions.delete')}
                    </Button>
                )}
                {selected && (
                    <Button variant="ghost" onClick={onCancel} data-testid="broadcasts-cancel-btn">
                        {t('broadcasts.form.cancel')}
                    </Button>
                )}
            </div>
            {selected && (
                <div className="text-[11px] text-muted-foreground pt-1">
                    {selected.published_at ? `${t('broadcasts.form.published_at')}: ${new Date(selected.published_at).toLocaleString()}` : null}
                </div>
            )}
        </div>
    );
};

const SelectField = ({ label, value, onChange, options, testId }) => (
    <div>
        <Label>{label}</Label>
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
            <SelectContent>
                {options.map(([val, lbl]) => (
                    <SelectItem key={val} value={val} data-testid={`${testId}-opt-${val}`}>{lbl}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

export default AdminBroadcasts;
